// Customer Order Detail — Square checkout success / order tracking page.
// Routed at /account/orders/:order_id. Shows payment status, fulfillment
// status, tracking placeholder, and a per-line itemized breakdown. Never
// displays raw Square JSON, payment IDs, or internal provider details.

import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, Loader2, Package, Truck, MapPin, ShieldCheck,
  ArrowLeft, Receipt, AlertCircle,
} from "lucide-react";
import api from "@/lib/api";

const FULFILLMENT_LABELS = {
  awaiting_payment:         { label: "Awaiting payment",          tone: "amber"  },
  awaiting_lifted_approval: { label: "Awaiting Lifted approval",  tone: "amber"  },
  pending_dispatch:         { label: "Pending dispatch",          tone: "amber"  },
  preparing:                { label: "Preparing your order",      tone: "amber"  },
  ready_to_ship:            { label: "Ready to ship",             tone: "amber"  },
  shipped:                  { label: "Shipped",                   tone: "green"  },
  out_for_delivery:         { label: "Out for delivery",          tone: "green"  },
  delivered:                { label: "Delivered",                 tone: "green"  },
  cancelled:                { label: "Cancelled",                 tone: "rose"   },
  refunded:                 { label: "Refunded",                  tone: "rose"   },
  returned:                 { label: "Returned",                  tone: "rose"   },
};

function formatMoney(cents, currency = "USD") {
  const v = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v);
}

function StatusBadge({ status }) {
  const meta = FULFILLMENT_LABELS[status] || {
    label: (status || "—").replace(/_/g, " "),
    tone: "amber",
  };
  const tone =
    meta.tone === "green" ? "bg-lifted-green/20 text-lifted-green border-lifted-green/40"
      : meta.tone === "rose" ? "bg-rose-500/20 text-rose-200 border-rose-500/40"
      : "bg-amber-500/20 text-amber-200 border-amber-500/40";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.18em] font-bebas ${tone}`}
      data-testid="order-fulfillment-status"
    >
      {meta.label}
    </span>
  );
}

export default function OrderDetail() {
  const { order_id } = useParams();
  const [params] = useSearchParams();
  const isFreshSuccess = params.get("success") === "true";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!order_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/customer/orders/${order_id}`);
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          setErr(
            status === 404
              ? "We could not find this order on your account."
              : "We could not load this order. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [order_id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="order-detail-loading">
        <Loader2 className="h-8 w-8 mx-auto animate-spin text-lifted-green" />
        <p className="text-lifted-dim text-sm mt-3">Loading your order…</p>
      </div>
    );
  }

  if (err || !order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="order-detail-error">
        <AlertCircle className="h-10 w-10 mx-auto text-rose-300" />
        <h1 className="font-anton text-3xl uppercase mt-3 text-white">Order not found</h1>
        <p className="text-lifted-dim mt-2 text-sm">{err || "We could not find this order."}</p>
        <Link
          to="/account"
          className="btn-lifted mt-6 inline-flex items-center gap-2"
          data-testid="order-back-to-account"
        >
          <ArrowLeft size={14} /> Back to your account
        </Link>
      </div>
    );
  }

  const paid = order.payment_status === "paid" || order.status === "paid";
  const fulfillmentType = (order.fulfillment_type || "").toLowerCase();
  const isSameDay =
    fulfillmentType === "same_day_delivery" ||
    fulfillmentType === "same_day" ||
    fulfillmentType === "local_delivery";
  const hasTracking = !!order.tracking_number;

  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6"
      data-testid="customer-order-detail"
    >
      {/* Top breadcrumb + success banner */}
      <div className="flex items-center justify-between">
        <Link
          to="/account"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bebas text-lifted-dim hover:text-lifted-green"
          data-testid="order-detail-back"
        >
          <ArrowLeft size={14} /> Your account
        </Link>
        <span className="text-[10px] text-lifted-dim font-mono">
          ORDER #{(order.id || "").slice(0, 8).toUpperCase()}
        </span>
      </div>

      {isFreshSuccess && paid && (
        <div
          className="glass-panel p-5 border border-lifted-green/40 bg-lifted-green/5 flex items-start gap-3"
          data-testid="order-success-banner"
        >
          <CheckCircle2 className="h-6 w-6 text-lifted-green flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-anton uppercase text-lg text-white">Thank you for your order</div>
            <p className="text-lifted-dim text-sm mt-1">
              Your payment was received. You'll get an email confirmation shortly with order details.
            </p>
          </div>
        </div>
      )}

      {/* Status header */}
      <header className="glass-panel p-6 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow text-lifted-dim">Order summary</div>
            <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white mt-1">
              {formatMoney(order.amount_cents, order.currency)}
            </h1>
            <p className="text-xs text-lifted-dim mt-1">
              Placed {(order.created_at || "").slice(0, 10) || "—"}
              {" · "}
              {order.items?.length || 0}{" "}
              {(order.items?.length || 0) === 1 ? "item" : "items"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.18em] font-bebas bg-lifted-green/15 text-lifted-green border-lifted-green/40"
              data-testid="order-payment-status"
            >
              <ShieldCheck size={12} /> {paid ? "Paid" : (order.payment_status || "pending")}
            </span>
            <StatusBadge status={order.fulfillment_status} />
          </div>
        </div>
      </header>

      {/* Fulfillment / Tracking */}
      <section className="glass-panel p-6" data-testid="order-fulfillment-card">
        <div className="flex items-center gap-2 mb-3">
          {isSameDay ? <Truck className="h-4 w-4 text-lifted-green" />
                      : <Package className="h-4 w-4 text-lifted-green" />}
          <h2 className="font-anton uppercase text-lg text-white">
            {isSameDay ? "Same-day delivery" : "Mail shipping"}
          </h2>
        </div>
        {hasTracking ? (
          <div className="text-sm space-y-2" data-testid="order-tracking-info">
            <div>
              <span className="text-lifted-dim">Tracking number: </span>
              <span className="text-white font-mono" data-testid="order-tracking-number">
                {order.tracking_number}
              </span>
            </div>
            {order.tracking_carrier && (
              <div>
                <span className="text-lifted-dim">Carrier: </span>
                <span className="text-white">{order.tracking_carrier}</span>
              </div>
            )}
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-lifted-green hover:underline text-xs uppercase tracking-[0.16em] font-bebas mt-2"
                data-testid="order-tracking-link"
              >
                Track this shipment →
              </a>
            )}
          </div>
        ) : (
          <div
            className="text-sm text-lifted-dim flex items-start gap-2"
            data-testid="order-tracking-placeholder"
          >
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              {isSameDay
                ? "Your same-day order is in our dispatch queue. We'll notify you with a driver assignment shortly."
                : "Your order is awaiting Lifted approval. We'll attach a tracking number as soon as it ships."}
            </p>
          </div>
        )}

        {order.shipping_address && (
          <div className="mt-5 pt-4 border-t border-white/10 text-sm">
            <div className="eyebrow text-lifted-dim text-xs">Shipping to</div>
            <div className="text-white mt-1 text-sm leading-relaxed">
              {[
                order.shipping_address.name,
                order.shipping_address.line1,
                order.shipping_address.line2,
                [order.shipping_address.city,
                 order.shipping_address.state,
                 order.shipping_address.postal_code]
                  .filter(Boolean).join(", "),
              ].filter(Boolean).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Items */}
      <section className="glass-panel p-6" data-testid="order-items-card">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="h-4 w-4 text-lifted-green" />
          <h2 className="font-anton uppercase text-lg text-white">Items</h2>
        </div>
        {order.items?.length ? (
          <ul className="divide-y divide-white/5" data-testid="order-items-list">
            {order.items.map((it, idx) => (
              <li
                key={`${it.handle || idx}-${idx}`}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`order-item-${idx}`}
              >
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">
                    {it.title || it.name || it.handle}
                  </div>
                  {it.variant_title && (
                    <div
                      className="text-[10px] text-lifted-green font-bebas tracking-[0.18em] uppercase"
                      data-testid={`order-item-variant-${idx}`}
                    >
                      Variant: {it.variant_title}
                    </div>
                  )}
                  <div className="text-[11px] text-lifted-dim">
                    Qty {it.qty || 1}
                    {it.handle && <> · {it.handle}</>}
                    {it.sku && <> · SKU {it.sku}</>}
                  </div>
                </div>
                <div className="text-sm font-mono text-white">
                  {formatMoney(
                    it.line_total_cents
                      || it.line_cents
                      || it.subtotal_cents
                      || ((it.unit_cents || it.price_cents || 0) * (it.qty || 1)),
                    order.currency
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-lifted-dim">No item details recorded.</p>
        )}

        {/* Totals breakdown */}
        <div className="mt-5 pt-4 border-t border-white/10 text-sm space-y-1.5">
          {!!order.subtotal_cents && (
            <Row label="Subtotal" value={formatMoney(order.subtotal_cents, order.currency)} />
          )}
          {!!order.shipping_fee_cents && (
            <Row label="Shipping" value={formatMoney(order.shipping_fee_cents, order.currency)} />
          )}
          {!!order.delivery_fee_cents && (
            <Row label="Same-day delivery" value={formatMoney(order.delivery_fee_cents, order.currency)} />
          )}
          {!!order.tax_cents && (
            <Row label="Tax" value={formatMoney(order.tax_cents, order.currency)} />
          )}
          <div className="pt-2 border-t border-white/10 mt-2 flex items-baseline justify-between">
            <span className="font-anton uppercase text-white">Total</span>
            <span
              className="font-anton text-xl text-lifted-green"
              data-testid="order-total"
            >
              {formatMoney(order.amount_cents, order.currency)}
            </span>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-2 pt-2">
        <Link
          to="/account"
          className="btn-lifted inline-flex items-center gap-2"
          data-testid="order-detail-account-cta"
        >
          <Package size={14} /> All your orders
        </Link>
        <Link
          to="/shop"
          className="btn-ghost"
          data-testid="order-detail-shop-cta"
        >
          Continue shopping
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-lifted-dim">{label}</span>
      <span className="text-white font-mono">{value}</span>
    </div>
  );
}
